import {
  getScrollTop,
  scrollTo,
  getScrollEventTarget,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

class ScrollToTopButton extends HTMLElement {
  /** @type {AbortController} */
  #rootAbort = new AbortController();

  /** @type {AbortController | null} */
  #scrollAbort = null;

  /** @type {number | null} */
  #rafId = null;

  /** @type {number} */
  #threshold = 250;

  /** @type {HTMLButtonElement | null} */
  #button = null;

  connectedCallback() {
    this.#threshold = Number(this.dataset.threshold) || 250;
    this.#button = this.querySelector('button');

    const { signal } = this.#rootAbort;

    this.#bindScrollListener();
    scrollContainerMediaQuery.addEventListener('change', this.#handleScrollContainerChange, { signal });
    this.#button?.addEventListener('click', this.#handleClick, { signal });

    this.#updateVisibility();
  }

  disconnectedCallback() {
    this.#rootAbort.abort();
    this.#scrollAbort?.abort();
    if (this.#rafId != null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }

  #handleScrollContainerChange = () => {
    this.#bindScrollListener();
    this.#updateVisibility();
  };

  #bindScrollListener() {
    this.#scrollAbort?.abort();
    this.#scrollAbort = new AbortController();

    getScrollEventTarget().addEventListener('scroll', this.#handleScroll, {
      passive: true,
      signal: this.#scrollAbort.signal,
    });
  }

  #handleScroll = () => {
    if (this.#rafId != null) return;

    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#updateVisibility();
    });
  };

  #updateVisibility() {
    const visible = getScrollTop() >= this.#threshold;

    if (visible) {
      this.dataset.visible = '';
      this.#button?.setAttribute('aria-hidden', 'false');
      this.#button?.removeAttribute('tabindex');
    } else {
      delete this.dataset.visible;
      this.#button?.setAttribute('aria-hidden', 'true');
      this.#button?.setAttribute('tabindex', '-1');
    }
  }

  #handleClick = () => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    scrollTo({ top: 0, behavior: reducedMotion ? 'instant' : 'smooth' });
  };
}

if (!customElements.get('scroll-to-top-button')) {
  customElements.define('scroll-to-top-button', ScrollToTopButton);
}
